import { ParamMap } from '@angular/router';

import { URIPrefix } from '../enums/url-prefix.enum';
import { UrlQueryParams } from '../enums/url-query-params.enum';

/** Regex: "." only when followed by a category prefix (so values may contain "."). */
const prefixPattern = Object.values(URIPrefix).join('|');
const CATEGORY_DELIMITER_REGEX = new RegExp(`\\.(?=${prefixPattern}-)`);

/**
 * Maps legacy session type URL values to current values.
 * Supports backwards compatibility for URLs created before MER-4849.
 */
const LEGACY_SESSION_TYPE_URL_MAP: Record<string, string> = {
  'online therapy': 'video',
  'online%20therapy': 'video',
} as const;

export interface IFilterEntry {
  key: string;
  value: string;
}

/**
 * Parses a filter string into categorized key-value entries.
 * Preserves category grouping (`.`-delimited) and intra-category ordering (`,`-delimited).
 * Splits on the first `-` only, so values like "Non-binary" or "EAP (CO)" are kept intact.
 */
export const parseFilterEntries = (filterString: string): IFilterEntry[][] => filterString
  .split(CATEGORY_DELIMITER_REGEX)
  .map((segment) => segment.split(',').reduce<IFilterEntry[]>((entries, kvp) => {
    const dashIdx = kvp.indexOf('-');
    if (dashIdx <= 0) return entries;

    const key = kvp.substring(0, dashIdx);
    const value = kvp.substring(dashIdx + 1);
    if (value) {
      entries.push({ key, value });
    }
    return entries;
  }, []));

/* eslint-disable no-restricted-syntax */
export type DirectoryParsedParams = {
  [URIPrefix.STATE]: string | null;
  [URIPrefix.CITY]: string | null;
  [URIPrefix.INSURANCE]: string;
  [URIPrefix.MODALITY]: string[];
  [URIPrefix.SPECIALTY]: string[];
  [URIPrefix.GENDER]: string[];
  [URIPrefix.TREATMENT_PREFERENCE]: string[];
  [URIPrefix.AGE]: string[];
  [URIPrefix.SESSION_TYPE]: string[];
};

export const ParseDirectoryFilterParams = (paramMap: ParamMap): DirectoryParsedParams => {
  // example URI - /sta-CO/cty-all/ins-aetna;mod-group,mod-family;spc-depression,spc-anxiety;gdr-man
  // Strict prefix match: only "sta-" and "cty-" slugs are recognized. Anything else (typos like
  // "st-CO", unknown prefixes) returns null so downstream consumers don't silently use bad data.
  const stateSlug = paramMap.get(UrlQueryParams.STATE);
  const state = stateSlug?.startsWith(`${URIPrefix.STATE}-`)
    ? stateSlug.slice(URIPrefix.STATE.length + 1).toUpperCase()
    : undefined;
  // Treat any remaining hyphens as word separators so multi-word slugs (e.g. "cty-grand-rapids",
  // "cty-king-of-prussia") match the space-separated form the directory API uses.
  const citySlug = paramMap.get(UrlQueryParams.CITY);
  const city = citySlug?.startsWith(`${URIPrefix.CITY}-`)
    ? citySlug.slice(URIPrefix.CITY.length + 1).replace(/-/g, ' ')
    : undefined;
  const params: DirectoryParsedParams = {
    [URIPrefix.STATE]: state || null,
    [URIPrefix.CITY]: city ? decodeURIComponent(city) : null,
    [URIPrefix.INSURANCE]: '',
    [URIPrefix.MODALITY]: [],
    [URIPrefix.SPECIALTY]: [],
    [URIPrefix.GENDER]: [],
    [URIPrefix.TREATMENT_PREFERENCE]: [],
    [URIPrefix.AGE]: [],
    [URIPrefix.SESSION_TYPE]: []
  };
  const filters = paramMap.get(UrlQueryParams.FILTERS);
  if (filters) {
    parseFilterEntries(filters).forEach((entries) => {
      entries.forEach(({ key, value }) => {
        if (key === URIPrefix.INSURANCE) {
          params[URIPrefix.INSURANCE] = decodeURIComponent(value);
        } else if (
          [
            URIPrefix.MODALITY,
            URIPrefix.SPECIALTY,
            URIPrefix.GENDER,
            URIPrefix.SESSION_TYPE,
            URIPrefix.AGE,
            URIPrefix.TREATMENT_PREFERENCE
          ].includes(key as URIPrefix)
        ) {
          let decodedValue = decodeURIComponent(value);

          // Normalize legacy session type values to current ones
          if (key === URIPrefix.SESSION_TYPE) {
            const normalizedValue = LEGACY_SESSION_TYPE_URL_MAP[decodedValue.toLowerCase()];
            if (normalizedValue) {
              decodedValue = normalizedValue;
            }
          }

          (params[key as URIPrefix] as string[]).push(decodedValue);
        }
      });
    });
  }
  return params;
};

export const ValidateDirectoryParams = (input: string): boolean => {
  // Early return for empty string (all categories are optional)
  if (!input || input.trim() === '') {
    return true;
  }

  // Split on "." only when followed by a category prefix, so "." is allowed in values
  const categories = input.split(CATEGORY_DELIMITER_REGEX).filter((cat) => cat.trim() !== '');

  // Define patterns for each category
  const patterns = {
    ins: /^ins-.+$/,
    mod: /^mod-.+$/,
    spc: /^spc-.+$/,
    gdr: /^gdr-.+$/,
    tap: /^tap-.+$/,
    age: /^age-.+$/,
    sess: /^sess-.+$/
  };

  // Keep track of categories encountered
  const categoryCount: { [key: string]: number; } = {};

  for (const category of categories) {
    // Split category items by commas
    const items = category.split(',').filter((item) => item.trim() !== '');

    if (items.length === 0) {
      return false; // Empty category is invalid
    }

    // Determine the category type based on the prefix of the first item
    const firstItem = items[0].trim();
    let categoryType: string | null = null;

    for (const key in patterns) {
      if (patterns[key as keyof typeof patterns].test(firstItem)) {
        categoryType = key;
        break;
      }
    }

    if (!categoryType) {
      // Invalid category type
      return false;
    }

    // Update category count
    categoryCount[categoryType] = (categoryCount[categoryType] || 0) + 1;

    // Check for multiple ins-* categories
    if (categoryType === 'ins' && categoryCount['ins'] > 1) {
      return false; // Only one ins-* category allowed
    }

    // Validate each item in the category
    const pattern = patterns[categoryType as keyof typeof patterns];
    for (const item of items) {
      if (!pattern.test(item.trim())) {
        return false; // Item does not match the required pattern
      }
    }
  }

  return true;
};
